import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { profileAPI } from '../../services/api'
import {
  ScaleIcon,
  HeartIcon,
  FireIcon,
  InformationCircleIcon,
  BeakerIcon
} from '@heroicons/react/24/outline'

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary', description: 'Little or no exercise' },
  { value: 'light', label: 'Light', description: 'Light exercise 1-3 days/week' },
  { value: 'moderate', label: 'Moderate', description: 'Moderate exercise 3-5 days/week' },
  { value: 'active', label: 'Active', description: 'Hard exercise 6-7 days/week' },
  { value: 'very_active', label: 'Very Active', description: 'Very hard exercise, physical job' }
]

export default function HealthMetricsCard() {
  const { t } = useTranslation()
  const [activityLevel, setActivityLevel] = useState('moderate')

  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['healthMetrics', activityLevel],
    queryFn: () => profileAPI.getHealthMetrics(activityLevel),
    select: (response) => response.data
  })

  const getBMIColor = (category) => {
    switch (category) {
      case 'underweight': return 'text-blue-600 bg-blue-100'
      case 'normal': return 'text-green-600 bg-green-100'
      case 'overweight': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-red-600 bg-red-100'
    }
  }

  const getBodyFatColor = (category) => {
    switch (category) {
      case 'essential': return 'text-blue-600 bg-blue-100'
      case 'athletic': return 'text-green-600 bg-green-100'
      case 'fitness': return 'text-emerald-600 bg-emerald-100'
      case 'average': return 'text-yellow-600 bg-yellow-100'
      case 'obese': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.healthMetrics', 'Health Metrics')}
          </h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.healthMetrics', 'Health Metrics')}
          </h3>
        </div>
        <div className="card-body">
          <p className="text-red-600">{t('common.error', 'Error loading data')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">
          {t('profile.healthMetrics', 'Health Metrics')}
        </h3>
      </div>
      <div className="card-body space-y-6">
        {/* Missing fields warning */}
        {!metrics?.can_calculate && metrics?.missing_fields?.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-800">
                  {t('profile.missingFieldsWarning', 'To calculate all metrics, please update your profile with:')}
                </p>
                <ul className="list-disc list-inside text-sm text-yellow-700 mt-1">
                  {metrics?.missing_fields.map(field => (
                    <li key={field}>{t(`profile.fields.${field}`, field.replace('_', ' '))}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* BMI Card */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ScaleIcon className="h-8 w-8 text-indigo-600 mr-3" />
              <div>
                <p className="text-sm text-gray-500">{t('profile.bmi', 'BMI')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics?.bmi ? metrics.bmi.toFixed(1) : '-'}
                </p>
              </div>
            </div>
            {metrics?.bmi_category && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBMIColor(metrics.bmi_category)}`}>
                {t(`profile.bmiCategories.${metrics.bmi_category}`, metrics.bmi_category)}
              </span>
            )}
          </div>
          {metrics?.bmi_description && (
            <p className="text-sm text-gray-600 mt-2">{metrics.bmi_description}</p>
          )}
        </div>

        {/* Body Composition Card */}
        {metrics?.body_fat_percentage && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BeakerIcon className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">
                    {t('profile.bodyFat', 'Body Fat')}
                    {metrics.body_fat_source === 'measured' && (
                      <span className="ml-1 text-xs text-green-600">({t('profile.measured', 'measured')})</span>
                    )}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metrics.body_fat_percentage.toFixed(1)}%
                  </p>
                </div>
              </div>
              {metrics?.body_fat_category && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBodyFatColor(metrics.body_fat_category)}`}>
                  {t(`profile.bodyFatCategories.${metrics.body_fat_category}`, metrics.body_fat_category)}
                </span>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('profile.leanMass', 'Lean Mass')}:</span>
                <span className="ml-1 font-medium text-gray-900">{metrics.lean_mass_kg} kg</span>
              </div>
              <div>
                <span className="text-gray-500">{t('profile.fatMass', 'Fat Mass')}:</span>
                <span className="ml-1 font-medium text-gray-900">{metrics.fat_mass_kg} kg</span>
              </div>
              {metrics?.muscle_mass_kg && (
                <div>
                  <span className="text-gray-500">{t('profile.muscleMass', 'Muscle Mass')}:</span>
                  <span className="ml-1 font-medium text-gray-900">{metrics.muscle_mass_kg} kg</span>
                </div>
              )}
            </div>

            {/* Body Fat Ranges Info */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">
                {t('profile.idealRanges', 'Ideal Ranges')} ({metrics.gender === 'male' ? t('profile.genders.male', 'Male') : t('profile.genders.female', 'Female')}):
              </p>
              <div className="grid grid-cols-5 gap-1 text-xs">
                {(metrics.gender === 'male' ? [
                  { cat: 'essential', range: '2-5%', color: 'bg-blue-100 text-blue-700' },
                  { cat: 'athletic', range: '6-13%', color: 'bg-green-100 text-green-700' },
                  { cat: 'fitness', range: '14-17%', color: 'bg-emerald-100 text-emerald-700' },
                  { cat: 'average', range: '18-24%', color: 'bg-yellow-100 text-yellow-700' },
                  { cat: 'obese', range: '25%+', color: 'bg-red-100 text-red-700' }
                ] : [
                  { cat: 'essential', range: '10-13%', color: 'bg-blue-100 text-blue-700' },
                  { cat: 'athletic', range: '14-20%', color: 'bg-green-100 text-green-700' },
                  { cat: 'fitness', range: '21-24%', color: 'bg-emerald-100 text-emerald-700' },
                  { cat: 'average', range: '25-31%', color: 'bg-yellow-100 text-yellow-700' },
                  { cat: 'obese', range: '32%+', color: 'bg-red-100 text-red-700' }
                ]).map(({ cat, range, color }) => (
                  <div
                    key={cat}
                    className={`text-center p-1 rounded ${color} ${metrics.body_fat_category === cat ? 'ring-2 ring-purple-500 ring-offset-1' : ''}`}
                  >
                    <div className="font-medium">{range}</div>
                    <div className="truncate">{t(`profile.bodyFatCategories.${cat}`, cat)}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('profile.healthyRange', 'Healthy range')}: {metrics.gender === 'male' ? '10-20%' : '18-28%'}
              </p>
            </div>

            {metrics.body_fat_source === 'calculated' && (
              <p className="text-xs text-gray-500 mt-2 italic">
                {t('profile.bodyFatEstimate', 'Estimate based on Deurenberg formula')}
              </p>
            )}
          </div>
        )}

        {/* BMR Card */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <HeartIcon className="h-8 w-8 text-red-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">{t('profile.bmr', 'Basal Metabolic Rate')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics?.bmr_measured
                    ? `${Math.round(metrics.bmr_measured)} ${t('common.kcal', 'kcal')}`
                    : metrics?.bmr
                      ? `${Math.round(metrics.bmr)} ${t('common.kcal', 'kcal')}`
                      : '-'}
                </p>
              </div>
            </div>
            {metrics?.bmr_measured && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                {t('profile.measured', 'measured')}
              </span>
            )}
          </div>
          {metrics?.bmr_measured && metrics?.bmr && (
            <p className="text-xs text-gray-500 mt-2">
              {t('profile.calculatedBmr', 'Calculated')}: {Math.round(metrics.bmr)} kcal
            </p>
          )}
          <p className="text-sm text-gray-600 mt-2">
            {t('profile.bmrDescription', 'Calories your body needs at complete rest')}
          </p>
        </div>

        {/* TDEE Card */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <FireIcon className="h-8 w-8 text-orange-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">{t('profile.tdee', 'Daily Energy Needs')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics?.tdee ? `${Math.round(metrics.tdee)} ${t('common.kcal', 'kcal')}` : '-'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.activityLevel', 'Activity Level')}
            </label>
            <select
              value={activityLevel}
              onChange={(e) => setActivityLevel(e.target.value)}
              className="form-select w-full"
            >
              {ACTIVITY_LEVELS.map(level => (
                <option key={level.value} value={level.value}>
                  {t(`profile.activityLevels.${level.value}`, level.label)} - {level.description}
                </option>
              ))}
            </select>
          </div>

          {metrics?.activity_multiplier && (
            <p className="text-sm text-gray-500 mt-2">
              {t('profile.multiplier', 'Multiplier')}: {metrics.activity_multiplier}x
            </p>
          )}
        </div>

        {/* User data summary */}
        {metrics?.weight_kg && (
          <div className="text-sm text-gray-500 border-t pt-4">
            <p>{t('profile.calculatedWith', 'Calculated with')}:</p>
            <p>
              {metrics.weight_kg && `${t('profile.weight', 'Weight')}: ${metrics.weight_kg}kg`}
              {metrics.height_cm && ` | ${t('profile.height', 'Height')}: ${metrics.height_cm}cm`}
              {metrics.age_years && ` | ${t('profile.age', 'Age')}: ${metrics.age_years}`}
              {metrics.gender && ` | ${t('profile.gender', 'Gender')}: ${metrics.gender}`}
            </p>
            {metrics.measured_at && (
              <p className="mt-1">
                {t('profile.lastMeasurement', 'Last measurement')}: {new Date(metrics.measured_at).toLocaleDateString()}
                {metrics.measurement_source === 'smart_scale' && (
                  <span className="ml-1 text-blue-600">({t('profile.sources.smart_scale', 'Smart Scale')})</span>
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
